"use client"
import { ThumbsUp, ThumbsDown, Send, MoreHorizontal, Edit, Trash, Flag, Reply, X, Check, User, Bot } from "lucide-react"
import { Link } from "react-router-dom"
import CommunityAIIcon from "../../components/AI/CommunityAIIcon"
import "../../components/AI/CommunityAIIcon.css"
import { useState, useRef, useEffect } from "react"
import ReactMarkdown from "react-markdown"
import rehypeRaw from "rehype-raw"
import remarkGfm from "remark-gfm"
import { toast } from "react-hot-toast"
import { auth } from "../../firebase"

// Helper function to format timestamps consistently
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "Unknown time";
  
  try {
    // Handle string timestamps
    if (typeof timestamp === "string") {
      return new Date(timestamp).toLocaleString();
    }
    // Handle Firebase Timestamp objects
    else if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleString();
    }
    // Handle regular Date objects
    else if (timestamp instanceof Date) {
      return timestamp.toLocaleString();
    }
    // Handle numeric timestamps (milliseconds)
    else if (typeof timestamp === "number") {
      return new Date(timestamp).toLocaleString();
    }
    // Fallback
    return "Unknown time";
  } catch (error) {
    console.error("Error formatting timestamp:", error);
    return "Invalid date";
  }
};

// Create a separate ReplyCard component
const ReplyCard = ({
  reply,
  commentId,
  userId,
  isNested = false,
  handleReplyVote,
  handleEditReply,
  handleDeleteReply,
  handleReportReply,
  handleNestedReply,
  setActiveDropdown,
  activeDropdown,
  editingReplyId,
  setEditingReplyId,
  editReplyText,
  setEditReplyText,
  renderReplyProfile,
  parentReplyId = null,
  nestingDepth = 1, // Track the nesting depth
  dataSource = "new",
}) => {
  const isReplyOwner = reply.createdBy === userId
  const [showDeepReplies, setShowDeepReplies] = useState(false)

  // If nesting is too deep (>4), collapse by default
  const isDeepNested = nestingDepth > 4

  // Get appropriate depth class
  const depthClass = nestingDepth <= 5 ? `depth-${nestingDepth}` : "depth-n"

  // Don't render anything if this is a deep reply and we're not showing them
  if (isDeepNested && !showDeepReplies) {
    return (
      <div className="nested-replies-deep">
        <button className="show-deep-replies-btn" onClick={() => setShowDeepReplies(true)}>
          <span>+ Show {reply.replies?.length || 0} more replies</span>
        </button>
      </div>
    )
  }

  return (
    <div className={`reply-card ${isNested ? "nested-reply" : ""}`}>
      {isNested && <div className="nesting-connector"></div>}
      <div className="reply-header">
        {renderReplyProfile(reply)}

        {/* Reply timestamp to show when it was posted */}
        <div className="reply-timestamp">
          {reply.createdAt
            ? typeof reply.createdAt === "string"
              ? new Date(reply.createdAt).toLocaleString()
              : reply.createdAt.toDate?.()
                ? reply.createdAt.toDate().toLocaleString()
                : "Unknown"
            : "Unknown"}
        </div>

        {/* 3-dot menu for replies */}
        <div className="comment-menu">
          <button
            className="comment-menu-btn"
            onClick={() => setActiveDropdown(activeDropdown === reply.id ? null : reply.id)}
            aria-label="Reply options"
          >
            <MoreHorizontal size={16} />
          </button>
          {activeDropdown === reply.id && (
            <div className="comment-menu-dropdown">
              {isReplyOwner && (
                <>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setEditingReplyId(reply.id)
                      setEditReplyText(reply.text)
                      setActiveDropdown(null)
                    }}
                  >
                    <Edit size={16} />
                    <span>Edit</span>
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      handleDeleteReply(commentId, reply.id, parentReplyId)
                      setActiveDropdown(null)
                    }}
                  >
                    <Trash size={16} />
                    <span>Delete</span>
                  </button>
                </>
              )}
              <button
                className="dropdown-item"
                onClick={() => {
                  handleReportReply(commentId, reply.id)
                  setActiveDropdown(null)
                }}
              >
                <Flag size={16} />
                <span>Report</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Show reply-to info */}
      {reply.replyingTo && (
        <div className="replying-to-wrapper">
          <span className="replying-to">Replying to @{reply.replyingTo}</span>
        </div>
      )}

      {/* Reply Edit UI */}
      {editingReplyId === reply.id ? (
        <div className="edit-reply-form">
          <textarea
            value={editReplyText}
            onChange={(e) => setEditReplyText(e.target.value)}
            className="edit-textarea"
          />
          <div className="edit-actions">
            <button
              onClick={() => handleEditReply(commentId, reply.id, editReplyText, parentReplyId)}
              className="edit-btn save-btn"
            >
              <Check size={14} />
              <span>Save</span>
            </button>
            <button onClick={() => setEditingReplyId(null)} className="edit-btn cancel-btn">
              <X size={14} />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      ) : (
        <p className="reply-text">{reply.text}</p>
      )}

      <div className="comment-actions">
        <button
          onClick={() => handleReplyVote(commentId, reply.id, "upvote")}
          className={`comment-vote-btn ${reply.votes && reply.votes[userId] === "upvote" ? "active" : ""}`}
        >
          <ThumbsUp size={14} />
          <span>{reply.upvoteCount || 0}</span>
        </button>

        <button
          onClick={() => handleReplyVote(commentId, reply.id, "downvote")}
          className={`comment-vote-btn ${reply.votes && reply.votes[userId] === "downvote" ? "active" : ""}`}
        >
          <ThumbsDown size={14} />
          <span>{reply.downvoteCount || 0}</span>
        </button>

        {/* Limit reply depth to prevent excessive nesting */}
        {userId && nestingDepth < 5 && (
          <button
            onClick={(e) => {
              e.preventDefault()
              handleNestedReply(reply.id)
            }}
            className="reply-btn simple"
          >
            <span>Reply</span>
          </button>
        )}
      </div>

      {/* Nested replies */}
      {reply.replies && reply.replies.length > 0 && (
        <div className={`nested-replies-container ${depthClass}`}>
          {reply.replies.map((nestedReply) => (
            <ReplyCard
              key={nestedReply.id}
              reply={nestedReply}
              commentId={commentId}
              userId={userId}
              isNested={true}
              handleReplyVote={handleReplyVote}
              handleEditReply={handleEditReply}
              handleDeleteReply={handleDeleteReply}
              handleReportReply={handleReportReply}
              handleNestedReply={handleNestedReply}
              setActiveDropdown={setActiveDropdown}
              activeDropdown={activeDropdown}
              editingReplyId={editingReplyId}
              setEditingReplyId={setEditingReplyId}
              editReplyText={editReplyText}
              setEditReplyText={setEditReplyText}
              renderReplyProfile={renderReplyProfile}
              parentReplyId={reply.id}
              nestingDepth={nestingDepth + 1} // Increment depth for nested replies
              dataSource={nestedReply.dataSource || dataSource}
            />
          ))}
        </div>
      )}

      {/* Button to hide deep replies if showing them */}
      {isDeepNested && showDeepReplies && (
        <button className="show-deep-replies-btn hide-replies" onClick={() => setShowDeepReplies(false)}>
          <span>− Hide deep replies</span>
        </button>
      )}
    </div>
  )
}

const CommentCard = ({
  comment,
  userId,
  handleCommentVote,
  handleEditComment,
  handleDeleteComment,
  handlePostReply,
  handleReplyVote,
  handleEditReply,
  handleDeleteReply,
  handleReportComment,
  handleReportReply,
  setReplyingCommentId,
  setReplyText,
  replyingCommentId,
  replyText,
  editingCommentId,
  setEditingCommentId,
  editText,
  setEditText,
  editingReplyId,
  setEditingReplyId,
  editReplyText,
  setEditReplyText,
  activeDropdown,
  setActiveDropdown,
}) => {
  const [replyingToId, setReplyingToId] = useState(null)
  const replyInputRef = useRef(null)

  // Add effect to scroll to the reply input when opened
  useEffect(() => {
    if (replyingCommentId === comment.id && replyInputRef.current) {
      // Scroll the reply input into view with a slight delay to ensure rendering
      setTimeout(() => {
        replyInputRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }, 100)
    }
  }, [replyingCommentId, comment.id])

  // If the comment has been removed, show the removed message
  if (comment.isHidden) {
    return (
      <div className="comment-card removed-content">
        <div className="removed-content-message">
          <p>
            <strong>This comment has been removed</strong> for violating our 
            <a href="/terms" target="_blank" rel="noopener noreferrer"> community guidelines</a>.
          </p>
          {comment.reportReason && (
            <p className="removal-reason">
              <strong>Reason:</strong> {comment.reportReason}
            </p>
          )}
        </div>
      </div>
    )
  }

  // Handle replying to a reply (nested reply)
  const handleNestedReply = (reply) => {
    setReplyingCommentId(comment.id === replyingCommentId ? null : comment.id)
    setReplyingToId(reply.id)
    setReplyText(`@${reply.commenterName}: `)

    // Scroll to reply form after state update
    setTimeout(() => {
      if (replyInputRef.current) {
        replyInputRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }
    }, 100)
  }

  // Function to render the reply profile with optional link to user profile
  const renderReplyProfile = (reply) => {
    // Check for null or undefined reply before accessing properties
    if (!reply) {
      return (
        <div className="commenter-info">
          <span className="commenter-name">Anonymous</span>
        </div>
      );
    }
    
    const commenterPhoto = reply.commenterPhoto || "/placeholder.svg";
    const commenterName = reply.commenterName || "Anonymous";
    const commenterId = reply.createdBy;
    const commenterBadge = reply.commenterBadge; // Get badge from reply data

    return (
      <div className="commenter-info">
        {commenterId && commenterId !== "AI_ASSISTANT" && commenterId !== "anonymous" ? (
          <Link to={`/users/${commenterId}`} className="profile-link">
            <img src={commenterPhoto} alt={commenterName} className="commenter-photo" />
            <span className="commenter-name">
              {commenterName}
              {/* Display badge */}
              {commenterBadge && <span className="commenter-badge">{commenterBadge}</span>}
            </span>
          </Link>
        ) : (
          <>
            <img src={commenterPhoto} alt={commenterName} className="commenter-photo" />
            <span className="commenter-name">
              {commenterName}
              {/* Display badge */}
              {commenterBadge && <span className="commenter-badge">{commenterBadge}</span>}
            </span>
          </>
        )}
        <span className="comment-timestamp">{formatTimestamp(reply.createdAt)}</span>
      </div>
    );
  }

  // Modify the handlePostReply function to include the parent reply ID if replying to a reply
  const postReplyWithContext = () => {
    handlePostReply(comment.id, replyText, replyingToId)
    setReplyingToId(null)
  }

  // Determine if the current user can edit/delete this comment
  const canModify = userId === comment.createdBy || userId === comment.queryOwnerId
  const currentVote = comment.votes ? comment.votes[userId] : null

  // New function to handle the start of a reply
  const handleReplyClick = () => {
    // Set the current comment as the one being replied to
    setReplyingCommentId(comment.id)

    // If replying to an AI response, prepopulate with the AI's name
    if (comment.isAIResponse) {
      setReplyText("@Community AI: ")
    } else {
      // Prepopulate with commenter's name for better context
      setReplyText(`@${comment.commenterName}: `)
    }
  }

  // The click handler for voting on a comment
  const handleVoteClick = async (type) => {
    if (!auth.currentUser) {
      toast.error("Please sign in to vote on comments");
      return;
    }
    
    if (comment.createdBy === auth.currentUser.uid) {
      toast.warning("You cannot vote on your own comments");
      return;
    }

    handleCommentVote(comment.id, type)
  }

  // Helper to render the comment author profile for consistent styling
  const renderCommentProfile = () => (
    <div className="commenter-info">
      {comment.isAIResponse ? (
        <div className="ai-commenter">
          <CommunityAIIcon size={28} />
          <span className="commenter-name">{comment.commenterName || "Community AI"}</span>
        </div>
      ) : (
        <>
          {comment.commenterPhoto ? (
            <>
              {comment.createdBy && comment.createdBy !== "AI_ASSISTANT" && comment.createdBy !== "anonymous" ? (
                <Link to={`/users/${comment.createdBy}`} className="profile-link">
                  <img
                    src={comment.commenterPhoto || "/placeholder.svg"}
                    alt={comment.commenterName}
                    className="commenter-photo"
                  />
                  <span className="commenter-name">{comment.commenterName || "Anonymous"}</span>
                </Link>
              ) : (
                <>
                  <img
                    src={comment.commenterPhoto || "/placeholder.svg"}
                    alt={comment.commenterName}
                    className="commenter-photo"
                  />
                  <span className="commenter-name">{comment.commenterName || "Anonymous"}</span>
                </>
              )}
            </>
          ) : (
            <>
              {comment.createdBy && comment.createdBy !== "AI_ASSISTANT" && comment.createdBy !== "anonymous" ? (
                <Link to={`/users/${comment.createdBy}`} className="profile-link">
                  <div className="commenter-avatar">
                    <User size={16} />
                  </div>
                  <span className="commenter-name">{comment.commenterName || "Anonymous"}</span>
                </Link>
              ) : (
                <>
                  <div className="commenter-avatar">
                    <User size={16} />
                  </div>
                  <span className="commenter-name">{comment.commenterName || "Anonymous"}</span>
                </>
              )}
            </>
          )}
        </>
      )}

      {comment.edited && <span className="edited-label">(edited)</span>}
    </div>
  )

  // Check if the reply feature is specifically disabled for this comment
  const isReplyDisabled = comment.disableReplies || false

  return (
    <div className={`comment-card ${comment.isAIResponse ? "ai-comment" : ""}`} id={`comment-${comment.id}`}>
      <div className="comment-header">
        {renderCommentProfile()}

        {/* AI Badge for AI-generated responses */}
        {comment.isAIResponse && (
          <div className="ai-badge">
            <Bot size={14} />
            <span>AI Generated</span>
            {/* Delete button only for query owner */}
            {userId === comment.queryOwnerId && (
              <button
                className="ai-delete-btn"
                onClick={() => handleDeleteComment(comment.id)}
                title="Delete AI response"
              >
                <Trash size={14} />
              </button>
            )}
          </div>
        )}

        {/* Comment timestamp */}
        <div className="comment-timestamp">
          {comment.createdAt
            ? typeof comment.createdAt === "string"
              ? new Date(comment.createdAt).toLocaleString()
              : comment.createdAt.toDate
                ? comment.createdAt.toDate().toLocaleString()
                : typeof comment.createdAt === "object" && comment.createdAt instanceof Date
                  ? comment.createdAt.toLocaleString()
                  : "Unknown"
            : "Unknown time"}
        </div>

        {/* 3-dot menu for actions */}
        {(!comment.isAIResponse || (comment.isAIResponse && userId === comment.queryOwnerId)) && (
          <div className="comment-menu">
            <button
              className="comment-menu-btn"
              onClick={() => setActiveDropdown(activeDropdown === comment.id ? null : comment.id)}
              aria-label="Comment options"
            >
              <MoreHorizontal size={16} />
            </button>
            {activeDropdown === comment.id && (
              <div className="comment-menu-dropdown">
                {canModify && !comment.isAIResponse && (
                  <>
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setEditingCommentId(comment.id)
                        setEditText(comment.text)
                        setActiveDropdown(null)
                      }}
                    >
                      <Edit size={14} />
                      <span>Edit</span>
                    </button>
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        handleDeleteComment(comment.id)
                        setActiveDropdown(null)
                      }}
                    >
                      <Trash size={14} />
                      <span>Delete</span>
                    </button>
                  </>
                )}
                {/* Allow query owner to delete AI responses */}
                {canModify && comment.isAIResponse && (
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      handleDeleteComment(comment.id)
                      setActiveDropdown(null)
                    }}
                  >
                    <Trash size={14} />
                    <span>Delete</span>
                  </button>
                )}
                {!canModify && (
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      handleReportComment(comment.id)
                      setActiveDropdown(null)
                    }}
                  >
                    <Flag size={14} />
                    <span>Report</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Comment content */}
      {editingCommentId === comment.id ? (
        <div className="edit-comment-form">
          <textarea className="edit-textarea" value={editText} onChange={(e) => setEditText(e.target.value)} />
          <div className="edit-actions">
            <button
              className="edit-btn cancel-btn"
              onClick={() => {
                setEditingCommentId(null)
                setEditText("")
              }}
            >
              <X size={14} />
              <span>Cancel</span>
            </button>
            <button className="edit-btn save-btn" onClick={() => handleEditComment(comment.id, editText)}>
              <Check size={14} />
              <span>Save</span>
            </button>
          </div>
        </div>
      ) : (
        <div className={`comment-content ${comment.isAIResponse ? "ai-content" : ""}`}>
          {comment.isAIResponse ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  return inline ? (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  ) : (
                    <pre className="code-block">
                      <code className={className} {...props}>
                        {children}
                      </code>
                    </pre>
                  )
                },
              }}
            >
              {comment.text}
            </ReactMarkdown>
          ) : (
            <div className="comment-text">{comment.text}</div>
          )}
        </div>
      )}

      {/* Comment actions (vote, reply) */}
      <div className="comment-actions">
        <button
          className={`comment-vote-btn ${currentVote === "upvote" ? "active" : ""}`}
          onClick={() => handleVoteClick("upvote")}
          disabled={!userId || userId === comment.createdBy}
        >
          <ThumbsUp size={14} />
          <span>{comment.upvoteCount || 0}</span>
        </button>
        <button
          className={`comment-vote-btn ${currentVote === "downvote" ? "active" : ""}`}
          onClick={() => handleVoteClick("downvote")}
          disabled={!userId || userId === comment.createdBy}
        >
          <ThumbsDown size={14} />
          <span>{comment.downvoteCount || 0}</span>
        </button>

        {/* Reply button - disabled for AI responses for now */}
        {!isReplyDisabled && (
          <button className="reply-btn" onClick={handleReplyClick} disabled={!userId}>
            <Reply size={14} />
            <span>Reply {comment.replies?.length > 0 ? `(${comment.replies.length})` : ""}</span>
          </button>
        )}
      </div>

      {/* Display replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="replies-container">
          {comment.replies.map((reply) => (
            <ReplyCard
              key={reply.id}
              reply={reply}
              commentId={comment.id}
              userId={userId}
              isNested={false} // Top-level replies
              handleReplyVote={handleReplyVote}
              handleEditReply={handleEditReply}
              handleDeleteReply={handleDeleteReply}
              handleReportReply={handleReportReply}
              handleNestedReply={handleNestedReply}
              setActiveDropdown={setActiveDropdown}
              activeDropdown={activeDropdown}
              editingReplyId={editingReplyId}
              setEditingReplyId={setEditingReplyId}
              editReplyText={editReplyText}
              setEditReplyText={setEditReplyText}
              renderReplyProfile={renderReplyProfile} // Pass down the profile renderer
              nestingDepth={1} // Top-level replies have depth 1
              dataSource={reply.dataSource || comment.dataSource} // Pass data source down
            />
          ))}
        </div>
      )}

      {/* Reply input form */}
      {replyingCommentId === comment.id && (
        <div className="reply-input" ref={replyInputRef}>
          <textarea
            className="reply-textarea"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a reply..."
          />
          <div className="reply-actions">
            <button
              className="reply-action-btn cancel-btn"
              onClick={() => {
                setReplyingCommentId(null)
                setReplyText("")
              }}
            >
              <span>Cancel</span>
            </button>
            <button
              className="reply-action-btn post-btn"
              onClick={postReplyWithContext}
              disabled={!replyText.trim()}
            >
              <Send size={14} />
              <span>Post</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CommentCard

